import { i18n } from '@lingui/core'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { AgenticMarkdown } from 'designSystem/AgenticMarkdown'
import { Text } from 'designSystem/components/Text'
import { ArtifactCard } from 'features/knowledgeDocumentViewer/artifacts/ArtifactCard'
import { useArtifactActions } from 'features/knowledgeDocumentViewer/artifacts/useArtifactActions'
import { CitedResponseSegments } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/CitedResponseSegments'
import { getDocChatTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import {
  DOC_CHAT_FALLBACK_NOTICE,
  DOC_CHAT_STOPPED_NOTICE,
  formatResultLabel,
  getDocChatErrorNotice,
  getDocChatResponseStatusText,
  isDocChatSearchPlanThought,
  shouldShowDocChatSearchStatus,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatText'
import type {
  DocChatArtifactEvent,
  DocChatEvent,
  DocChatResponseEvent,
  DocChatTurn,
} from 'features/knowledgeDocumentViewer/types'
import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { FallbackNotice, ResultCount, timelineItemEnter } from './docChatStyles'
import { DocChatTurnAttachments } from './DocChatTurnAttachments'

const markerPop = keyframes`
  0% {
    opacity: 0.35;
    transform: scale(0.55);
  }

  68% {
    opacity: 1;
    transform: scale(1.35);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const markerPulse = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in oklch, var(--foreground) 18%, transparent);
  }

  50% {
    box-shadow: 0 0 0 0.25rem transparent;
  }
`

const TimelineRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  align-items: start;
  min-height: 1.5rem;
  animation: ${timelineItemEnter} 180ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const MarkerCell = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
`

const MarkerDot = styled.span<{ $active?: boolean }>`
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: 9999rem;
  background: ${(props) =>
    props.$active ? 'var(--foreground)' : 'var(--muted-foreground)'};
  animation: ${markerPop} 180ms ease-out both;

  ${(props) =>
    props.$active &&
    css`
      animation:
        ${markerPop} 180ms ease-out both,
        ${markerPulse} 1.4s ease-out 180ms infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const ConnectorRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  height: 0.625rem;
`

const StatusText = styled.p`
  min-width: 0;
  margin: 0;
  color: var(--muted-foreground);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SubmittedQueryText = styled(AgenticMarkdown).attrs({ source: 'user' })`
  --foreground: var(--primary);
`

const SubmittedQueryStatusText = styled(StatusText).attrs({ as: 'div' })`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

const FinalResponseText = styled.div`
  min-width: 0;
  margin: 0;
  font-size: 1rem;
  line-height: 1.375rem;
  letter-spacing: 0;
`

const ResponseStatusText = styled(StatusText)`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

const ThoughtText = styled(StatusText)`
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: unset;
`

function AnimatedThinkingText(): React.ReactElement {
  useLingui()
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 400)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <StatusText aria-live='polite'>
      <Trans>Thinking{dots}</Trans>
    </StatusText>
  )
}

function SearchedStatusText({
  searchLabel,
}: {
  searchLabel: string
}): React.ReactElement {
  return (
    <SubmittedQueryStatusText>
      <Trans>
        Searched for <SubmittedQueryText>{searchLabel}</SubmittedQueryText>
      </Trans>
    </SubmittedQueryStatusText>
  )
}

function ResponseText({
  event,
}: {
  event: DocChatResponseEvent
}): React.ReactElement {
  useLingui()
  if (
    event.metadata.is_fallback ||
    event.inspection_results?.length ||
    event.cmms ||
    event.response_segments?.some((segment) =>
      segment.citations.some((citation) => citation.type === 'cmms_record')
    )
  ) {
    return (
      <ResponseStatusText title={event.metadata.query_plan.elasticsearch_query}>
        {getDocChatResponseStatusText(event)}
      </ResponseStatusText>
    )
  }
  const resultLabel = formatResultLabel(event.resultCount)
  return (
    <ResponseStatusText title={event.metadata.query_plan.elasticsearch_query}>
      <Trans>
        Updated search query and found <ResultCount>{resultLabel}</ResultCount>
      </Trans>
    </ResponseStatusText>
  )
}

function TimelineFallbackNotice(): React.ReactElement {
  useLingui()
  return (
    <FallbackNotice role='status'>
      <Text as='p' variant='label' color='default'>
        {i18n._(DOC_CHAT_FALLBACK_NOTICE)}
      </Text>
    </FallbackNotice>
  )
}

function TimelineEventContent({
  event,
  onOpenArtifact,
  onOpenArtifactInTab,
  artifactActionsDisabled,
}: {
  event: DocChatEvent
  onOpenArtifact: (event: DocChatArtifactEvent) => void
  onOpenArtifactInTab: (event: DocChatArtifactEvent) => void
  artifactActionsDisabled: boolean
}): React.ReactElement {
  useLingui()
  if (event.type === 'submitted_query') {
    return (
      <>
        <SearchedStatusText searchLabel={event.query} />
        <DocChatTurnAttachments attachments={event.attachments} />
      </>
    )
  }
  if (event.type === 'thinking') {
    return <AnimatedThinkingText />
  }
  if (event.type === 'thought') {
    return <ThoughtText>{event.message}</ThoughtText>
  }
  if (event.type === 'response') {
    return <ResponseText event={event} />
  }
  if (event.type === 'cancelled') {
    return <StatusText>{i18n._(DOC_CHAT_STOPPED_NOTICE)}</StatusText>
  }
  if (event.type === 'artifact') {
    return (
      <ArtifactCard
        event={event}
        variant='timeline'
        onOpen={onOpenArtifact}
        onOpenInTab={onOpenArtifactInTab}
        actionsDisabled={artifactActionsDisabled}
      />
    )
  }
  return (
    <StatusText>
      {getDocChatErrorNotice(
        event.type === 'error' ? event.message : undefined
      )}
    </StatusText>
  )
}

export function TimelineConnector(): React.ReactElement {
  return (
    <ConnectorRow>
      <div />
      <div />
    </ConnectorRow>
  )
}

function TimelineItem({
  active,
  children,
}: {
  active?: boolean
  children: React.ReactNode
}): React.ReactElement {
  return (
    <TimelineRow>
      <MarkerCell>
        <MarkerDot $active={active} />
      </MarkerCell>
      {children}
    </TimelineRow>
  )
}

function ResponseBodyRows({
  event,
}: {
  event: DocChatResponseEvent
}): React.ReactElement | null {
  if (event.metadata.is_fallback) {
    return (
      <>
        <TimelineConnector />
        <TimelineItem>
          <TimelineFallbackNotice />
        </TimelineItem>
      </>
    )
  }
  return (
    <CitedResponseSegments
      event={event}
      ResponseText={FinalResponseText}
      wrapItems={(items) =>
        items.map((item, index) => (
          <React.Fragment key={item.key ?? index}>
            <TimelineConnector />
            <TimelineItem>{item}</TimelineItem>
          </React.Fragment>
        ))
      }
    />
  )
}

function wrapPartialTimelineItems(
  isLoading: boolean,
  items: React.ReactElement[]
): React.ReactNode {
  return items.map((item, itemIndex) => (
    <React.Fragment key={item.key ?? itemIndex}>
      <TimelineConnector />
      <TimelineItem active={isLoading && itemIndex === items.length - 1}>
        {item}
      </TimelineItem>
    </React.Fragment>
  ))
}

/** Classic marker timeline for the header / landing conversation surface. */
export function DocChatTimelineTurnRows({
  turn,
}: {
  turn: DocChatTurn
}): React.ReactElement {
  const { canUseArtifacts, openInPanel, openInTab } = useArtifactActions()
  const status = getDocChatTurnStatus(turn)
  const isLoading = status === 'loading'
  const responseEvents = turn.pendingResponse
    ? [
        ...turn.events.filter(
          (event) =>
            event.type !== 'thinking' && event.type !== 'response_partial'
        ),
        turn.pendingResponse,
      ]
    : turn.events
  const events = responseEvents.filter(
    (event) =>
      !isDocChatSearchPlanThought(event) &&
      !(status === 'cancelled' && event.type === 'thought')
  )

  return (
    <>
      {events.map((event, index) => {
        if (event.type === 'response_partial') {
          return (
            <CitedResponseSegments
              key={event.id}
              event={event}
              ResponseText={FinalResponseText}
              wrapItems={(items) => wrapPartialTimelineItems(isLoading, items)}
            />
          )
        }
        if (
          event.type === 'response' &&
          !shouldShowDocChatSearchStatus(event)
        ) {
          return <ResponseBodyRows key={event.id} event={event} />
        }
        return (
          <React.Fragment key={event.id}>
            {index > 0 && <TimelineConnector />}
            <TimelineItem active={isLoading && index === events.length - 1}>
              <TimelineEventContent
                event={event}
                onOpenArtifact={openInPanel}
                onOpenArtifactInTab={openInTab}
                artifactActionsDisabled={!canUseArtifacts}
              />
            </TimelineItem>
            {event.type === 'response' && <ResponseBodyRows event={event} />}
          </React.Fragment>
        )
      })}
    </>
  )
}
